import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Custom-styled Datei-Upload für das Vereinbarungs-Dokument.
 * Kein Fluent UI Input — eigene Dropzone + Datei-Karte in CI-Design.
 */
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  sub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  // Visually hidden native input — keeps a11y/keyboard support.
  hiddenInput: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    ...shorthands.border("0"),
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6px",
    paddingTop: "22px",
    paddingBottom: "22px",
    paddingLeft: "16px",
    paddingRight: "16px",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1.5px", "dashed", BRAND.border),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "rgba(77,32,81,0.04)",
    },
    ":focus-within": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}22`,
    },
  },
  dropzoneActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "rgba(77,32,81,0.08)",
    transform: "translateY(-1px)",
  },
  iconWrap: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  primary: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 700,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  fileCard: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  fileIcon: {
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    ...shorthands.borderRadius("8px"),
    flexShrink: 0,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    flexGrow: 1,
    minWidth: 0,
  },
  fileName: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileMeta: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.74rem",
    color: BRAND.muted,
  },
  actions: {
    display: "flex",
    columnGap: "6px",
    flexShrink: 0,
  },
  actionBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "border-color, color, background-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      color: BRAND.purple,
    },
  },
  removeBtn: {
    ":hover": {
      ...shorthands.borderColor("#a1331c"),
      color: "#a1331c",
    },
  },
});

export type VereinbarungFile = {
  name: string;
  size: number;
  dataUrl?: string;
};

type Props = {
  file?: VereinbarungFile;
  onChange: (file: VereinbarungFile | null) => void;
  accept?: string;
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const readAsDataUrl = (file: File): Promise<string | undefined> =>
  new Promise((resolve) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => resolve(undefined);
      r.readAsDataURL(file);
    } catch {
      resolve(undefined);
    }
  });

const VereinbarungFileUpload = ({
  file,
  onChange,
  accept = ".pdf,.doc,.docx,image/*",
}: Props) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleSelect = async (selected: File | null) => {
    if (!selected) {
      onChange(null);
      return;
    }
    const dataUrl = await readAsDataUrl(selected);
    onChange({ name: selected.name, size: selected.size, dataUrl });
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    handleSelect(f);
    // reset, so picking the same file again still triggers
    e.target.value = "";
  };

  const openPicker = () => inputRef.current?.click();

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    handleSelect(f);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const previewable =
    !!file?.dataUrl && /\.(png|jpe?g|gif|webp|svg|pdf)$/i.test(file.name);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.label}>Vereinbarungs-Dokument</span>
        <span className={styles.sub}>
          PDF, Word oder Bild. Wird lokal mit dem Vertragsentwurf gespeichert.
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onInputChange}
        className={styles.hiddenInput}
        aria-label="Vereinbarungs-Dokument auswählen"
      />

      {file ? (
        <div className={styles.fileCard}>
          <span className={styles.fileIcon} aria-hidden>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M14 3v5h5" />
            </svg>
          </span>
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>{file.name}</span>
            <span className={styles.fileMeta}>{formatBytes(file.size)}</span>
          </div>
          <div className={styles.actions}>
            {previewable && file.dataUrl && (
              <a
                href={file.dataUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.actionBtn}
                style={{ textDecoration: "none" }}
              >
                Öffnen
              </a>
            )}
            <button
              type="button"
              className={styles.actionBtn}
              onClick={openPicker}
            >
              Ersetzen
            </button>
            <button
              type="button"
              className={mergeClasses(styles.actionBtn, styles.removeBtn)}
              onClick={() => onChange(null)}
            >
              Entfernen
            </button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={mergeClasses(styles.dropzone, dragOver && styles.dropzoneActive)}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <span className={styles.iconWrap} aria-hidden>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M17 8l-5-5-5 5" />
              <path d="M12 3v12" />
            </svg>
          </span>
          <span className={styles.primary}>Datei hierher ziehen oder auswählen</span>
          <span className={styles.hint}>
            PDF, DOC, DOCX oder Bild — max. 10 MB
          </span>
        </div>
      )}
    </div>
  );
};

export default VereinbarungFileUpload;
